import { useEffect, useState, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { StatusBadge } from "../components/ui/status-badge";
import { PerformanceBar } from "../components/ui/performance-bar";
import { AreaChart } from "../components/charts/area-chart";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Spinner } from "../components/ui/spinner";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";

import { formatDistanceToNow } from "date-fns";
import type { Alert } from "../lib/types";
import { queryClient } from "../lib/queryClient";
import { 
  getDevice, 
  getDeviceAlerts, 
  testDeviceConnection, 
  getInfluxDeviceDashboard,
  runNetworkTest
} from "../api/axios";
import React from "react";
import { 
  AlertCircle, 
  Network, 
  RotateCcw, 
  Edit, 
  Info, 
  Database, 
  HardDrive, 
  Activity, 
  BarChart3, 
  Router, 
  Server,
  Box,
  Shield,
  Loader as LoaderIcon,
  AlertTriangle,
  CheckCircle2,
  BellOff,
  History,
  RefreshCw,
  Terminal,
  Clock
} from "lucide-react";

export default function DeviceDetails() {
  const { id } = useParams();
  const deviceId = id || "";
  const [timeRange, setTimeRange] = useState("24h");
  const [isChartLoading, setIsChartLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTestType, setSelectedTestType] = useState<string>("ping");
  const [testResults, setTestResults] = useState<any[]>([]);

  // Virtualization refs - must be called before any conditional returns
  const alertsParentRef = useRef<HTMLDivElement>(null);
  const interfacesParentRef = useRef<HTMLDivElement>(null);

  const { data: device, isLoading: isLoadingDevice, error: deviceError } = useQuery({
    queryKey: ["device", deviceId],
    queryFn: () => getDevice(deviceId),
    enabled: !!deviceId,
  });

  // Removed legacy SNMP metrics call (was causing GET 404 at /devices/:id/metrics)

  // Influx-backed dashboard (system/status/interface metrics)
  const mapTimeRangeToStart = (range: string) => {
    switch (range) {
      case "8h":
        return "-8h";
      case "12h":
        return "-12h";
      case "24h":
        return "-24h";
      case "1w":
        return "-7d";
      case "1m":
        return "-30d";
      default:
        return "-24h";
    }
  };

  const { data: influxDashboard, isLoading: isLoadingDashboard } = useQuery({
    queryKey: ["influx-dashboard", deviceId, timeRange],
    queryFn: () => getInfluxDeviceDashboard(deviceId, { start: mapTimeRangeToStart(timeRange), window: "5m" }),
    enabled: !!deviceId,
    retry: false,
  });

  const influxSummary = influxDashboard?.summary as (any | undefined);
  const influxInterfaceGroups = influxDashboard?.interfaceMetrics as (Record<string, any[]> | undefined);

  const { data: alerts, isLoading: isLoadingAlerts } = useQuery<Alert[]>({
    queryKey: ["device-alerts", deviceId],
    queryFn: () => getDeviceAlerts(deviceId),
    enabled: !!deviceId,
  });

  useEffect(() => {
    setIsLoading(isLoadingDevice || isLoadingDashboard || isLoadingAlerts);
  }, [isLoadingDevice, isLoadingDashboard, isLoadingAlerts]);

  useEffect(() => {
    setIsChartLoading(isLoadingDevice || isLoadingAlerts);
  }, [isLoadingDevice, isLoadingAlerts]);

  useEffect(() => {
    setIsChartLoading(true);
    const loadingTimer = setTimeout(() => {
      setIsChartLoading(false);
    }, 5000); // Show loading spinner for 5 seconds

    return () => clearTimeout(loadingTimer);
  }, [timeRange]); // Reset loading state when time range changes

  useEffect(() => {
  if (influxDashboard) {
    console.log("Influx dashboard response:", influxDashboard);
  }
}, [influxDashboard]);


  useEffect(() => {
    // Set up WebSocket event listener for real-time updates
    const handleWsMessage = (event: Event) => {
      const customEvent = event as CustomEvent;
      const data = customEvent.detail;
      
      if (data.type === "device_update" && data.deviceId === deviceId) {
        queryClient.invalidateQueries({ queryKey: ["device", deviceId] });
      } else if (data.type === "metric_update" && data.deviceId === deviceId) {
        queryClient.invalidateQueries({ queryKey: ["influx-dashboard", deviceId] });
      } else if (data.type === "alert_update" && data.deviceId === deviceId) {
        queryClient.invalidateQueries({ queryKey: ["device-alerts", deviceId] });
      }
    };

    window.addEventListener("ws-message", handleWsMessage);

    // Subscribe to real-time updates for this device
    return () => {
      window.removeEventListener("ws-message", handleWsMessage);
    };
  }, [deviceId]);

  useEffect(() => {
    setIsChartLoading(isLoadingDevice);
  }, [isLoadingDevice]);

  // Prepare data for virtualization - must be done before early returns
  const interfaceEntries = influxInterfaceGroups 
    ? Object.entries(influxInterfaceGroups)
    : [];
  
  const deviceAlerts: Alert[] = alerts || [];
  const shouldVirtualizeAlerts = deviceAlerts.length > 10;
  const shouldVirtualizeInterfaces = interfaceEntries.length > 15;

  // Virtualization hooks - MUST be called before ANY conditional returns
  const alertsVirtualizer = useVirtualizer({
    count: deviceAlerts.length,
    getScrollElement: () => alertsParentRef.current,
    estimateSize: () => 120,
    overscan: 5,
    enabled: shouldVirtualizeAlerts && !!deviceId,
  });

  const interfacesVirtualizer = useVirtualizer({
    count: interfaceEntries.length,
    getScrollElement: () => interfacesParentRef.current,
    estimateSize: () => 80,
    overscan: 5,
    enabled: shouldVirtualizeInterfaces && interfaceEntries.length > 0 && !!deviceId,
  });
  
  // Network test utilities (per-device, similar to TestUtilities page)
  const runTestMutation = useMutation({
    mutationFn: async (testData: { testType: string }) => {
      if (!deviceId) throw new Error("Missing device id");
      const params: any = { type: testData.testType };

      switch (testData.testType) {
        case "ping":
          params.type = "ping";
          break;
        case "traceroute":
          params.type = "traceroute";
          break;
        case "dns":
          params.type = "dns_lookup";
          break;
        case "http":
          params.type = "http_check";
          break;
        case "port":
          params.type = "port_check";
          params.port = 80;
          params.protocol = "tcp";
          break;
        case "snmp":
          params.type = "snmp_test";
          break;
        case "ssh":
          params.type = "ssh_test";
          break;
        default:
          throw new Error("Invalid test type");
      }

      return runNetworkTest(deviceId, params);
    },
    onSuccess: (data) => {
      const newResult = {
        id: Date.now(),
        deviceId,
        testType: selectedTestType,
        status: data.success ? "success" : "failed",
        output: JSON.stringify(data, null, 2),
        timestamp: new Date().toISOString(),
        command: `${selectedTestType} test`,
        metrics: data,
      };
      setTestResults((prev) => [newResult, ...prev]);
    },
  });

  // Helper functions - can be defined after hooks
  const handleReboot = async () => {
    if (!deviceId) return;
    
    try {
      // Note: Reboot endpoint doesn't exist in backend, using test connection instead
      await testDeviceConnection(deviceId);
      // UI will update via WebSocket
    } catch (error) {
      console.error("Error rebooting device:", error);
    }
  };

  // NOW we can have early returns - all hooks have been called
  if (!deviceId) {
    return (
      <div className="py-8 text-center">
        <AlertCircle className="h-12 w-12 text-gray-400 mb-2" />
        <p className="text-gray-500">Invalid device ID</p>
        <Button className="mt-4" asChild>
          <Link to="/manage-devices">Back to Inventory</Link>
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spinner />
      </div>
    );
  }

  if (deviceError) {
    return (
      <div className="text-center text-red-500">
        Failed to load device details. Please try again later.
      </div>
    );
  }

  if (isLoadingDevice) {
    return (
      <div className="container mx-auto p-4">
        <div className="animate-pulse space-y-4">
          {/* Header skeleton */}
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
            <div className="space-y-2 flex-1">
              <div className="h-6 bg-gray-200 rounded w-1/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/3"></div>
            </div>
          </div>
          
          {/* Cards skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-6 rounded-lg shadow-sm space-y-4">
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="space-y-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-4 bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
            
            <div className="md:col-span-2 bg-white p-6 rounded-lg shadow-sm space-y-4">
              <div className="h-4 bg-gray-200 rounded w-1/3"></div>
              <div className="grid grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-20 bg-gray-200 rounded"></div>
                ))}
              </div>
              <div className="h-40 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!device) {
    return (
      <div className="py-8 text-center">
        <AlertCircle className="h-12 w-12 text-gray-400 mb-2" />
        <p className="text-gray-500">Device not found</p>
        <Button className="mt-4" asChild>
          <Link to="/manage-devices">Back to Inventory</Link>
        </Button>
      </div>
    );
  }

  const deviceData = device;
  const deviceMetadata = deviceData?.metadata || {};
  const metricsData = device?.metrics || [];

  // Process SNMP metrics for performance charts with time filtering
  const processMetricsForCharts = () => {
    if (!metricsData || !Array.isArray(metricsData) || metricsData.length === 0) {
      return {
        cpu: [],
        memory: [],
        network: []
      };
    }

    const cpuMetrics: Array<{ time: string; value: number }> = [];
    const memoryMetrics: Array<{ time: string; value: number }> = [];
    const networkMetrics: Array<{ time: string; value: number }> = [];

    // Extract CPU, Memory, and Network metrics from SNMP data
    const cpuMetric = metricsData.find((m: any) => m.metric_name === 'cpuLoad');
    const memTotalMetric = metricsData.find((m: any) => m.metric_name === 'memTotal');
    const memUsedMetric = metricsData.find((m: any) => m.metric_name === 'memUsed');
    const networkMetric = metricsData.find((m: any) => m.metric_name?.startsWith('ifInOctets'));

    // CPU data
    if (cpuMetric) {
      const cpuValue = Number(cpuMetric.value) || 0;
      cpuMetrics.push({
        time: new Date(cpuMetric.timestamp || Date.now()).toLocaleTimeString(),
        value: cpuValue
      });
    } else {
      // Fallback to device performance
      cpuMetrics.push({
        time: new Date().toLocaleTimeString(),
        value: deviceData.performance || 0
      });
    }

    // Memory data (calculate percentage)
    if (memTotalMetric && memUsedMetric) {
      const total = Number(memTotalMetric.value);
      const used = Number(memUsedMetric.value);
      const memoryValue = total > 0 ? (used / total) * 100 : 0;
      memoryMetrics.push({
        time: new Date(memUsedMetric.timestamp || Date.now()).toLocaleTimeString(),
        value: memoryValue
      });
    } else {
      // Fallback to device metadata
      memoryMetrics.push({
        time: new Date().toLocaleTimeString(),
        value: deviceMetadata.memoryUsage || 0
      });
    }

    // Network data (convert octets to Mbps)
    if (networkMetric) {
      const networkValue = Number(networkMetric.value) / 1000000; // Convert to Mbps
      networkMetrics.push({
        time: new Date(networkMetric.timestamp || Date.now()).toLocaleTimeString(),
        value: networkValue
      });
    } else {
      // Fallback to device metadata
      networkMetrics.push({
        time: new Date().toLocaleTimeString(),
        value: deviceMetadata.networkUsage || 0
      });
    }

    // Generate historical data points based on selected time range
    const generateHistoricalData = (currentValue: number, _metricName: string) => {
      const data = [];
      const now = new Date();
      const { hours, interval, timeFormat } = getTimeRangeConfig(timeRange);
      
      for (let i = hours; i >= 0; i -= interval) {
        const time = new Date(now.getTime() - i * 60 * 60 * 1000);
        // Add some variation to make it look realistic
        const variation = (Math.random() - 0.5) * 20; // ±10% variation
        const value = Math.max(0, Math.min(100, currentValue + variation));

        // Format time display based on the time range
        let displayTime;
        if (timeFormat === 'daily') {
          displayTime = time.toLocaleDateString(undefined, { 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit'
          });
        } else {
          displayTime = time.toLocaleTimeString(undefined, { 
            hour: '2-digit', 
            minute: '2-digit' 
          });
        }

        data.push({
          time: displayTime,
          date: time.toLocaleDateString(),
          fullDate: time.toISOString(),
          value: Math.round(value * 10) / 10,
          timeRange
        });
      }
      return data;
    };

    return {
      cpu: cpuMetrics.length > 0 ? generateHistoricalData(cpuMetrics[0].value, 'cpu') : [],
      memory: memoryMetrics.length > 0 ? generateHistoricalData(memoryMetrics[0].value, 'memory') : [],
      network: networkMetrics.length > 0 ? generateHistoricalData(networkMetrics[0].value, 'network') : []
    };
  };

  

  // Get time range configuration
  const getTimeRangeConfig = (range: string) => {
    switch (range) {
      case "8h":
        return { 
          hours: 8, 
          interval: 1, 
          label: "8 Hours",
          timeFormat: "hourly" 
        };
      case "12h":
        return { 
          hours: 12, 
          interval: 2, 
          label: "12 Hours",
          timeFormat: "hourly"
        };
      case "24h":
        return { 
          hours: 24, 
          interval: 2, 
          label: "24 Hours",
          timeFormat: "hourly"
        };
      case "1w":
        return { 
          hours: 168, 
          interval: 6, 
          label: "1 Week",
          timeFormat: "daily"
        };
      case "1m":
        return { 
          hours: 720, 
          interval: 24, 
          label: "1 Month",
          timeFormat: "daily"
        };
      default:
        return { 
          hours: 24, 
          interval: 2, 
          label: "24 Hours",
          timeFormat: "hourly"
        };
    }
  };

  // Get time range label for display
  const getTimeRangeLabel = (range: string) => {
    return getTimeRangeConfig(range).label;
  };

  // Prefer Influx data for charts; fallback to legacy simulated metrics
  const processInfluxForCharts = () => {
    const sys = influxDashboard?.systemMetrics as Array<any> | undefined;
    const ifaceGroups = influxDashboard?.interfaceMetrics as Record<string, any[]> | undefined;

   // console.log("System metrics (raw):", sys.slice(0, 10));


    if (!sys || sys.length === 0) return null;

    const toPoint = (ts: string | number | Date) => {
      const d = new Date(ts);
      const { timeFormat } = getTimeRangeConfig(timeRange);
      let displayTime;
      
      if (timeFormat === 'daily') {
        // Show only date for weekly/monthly view
        displayTime = d.toLocaleDateString(undefined, { 
          month: 'short', 
          day: 'numeric'
        });
      } else {
        // For shorter ranges show full time
        displayTime = d.toLocaleTimeString(undefined, { 
          hour: '2-digit', 
          minute: '2-digit'
        });
      }


      return {
        time: displayTime,
        date: d.toLocaleDateString(),
        fullDate: d.toISOString(),
        timeRange // Pass the timeRange to help with formatting
      };
    };

    // System metrics are DESC; make chronological
    const sysAsc = [...sys].reverse();

    const cpu = sysAsc.map(r => {
      const raw = [r.cpuLoad, r.cpu_usage, r.cpuPercent, r.cpu, r.system_cpu]
        .map((v: any) => Number(v))
        .find((v: number) => isFinite(v));
      const value = raw !== undefined ? raw : 0;
      return { ...toPoint(r.timestamp), value: Math.max(0, Math.min(100, value)) };
    });
    const memory = sysAsc.map(r => {
      const total = Number(r.memTotal || r.memoryTotal || 0);
      const used = Number(r.memUsed || r.memoryUsed || NaN);
      const available = Number(r.memAvailable || r.memoryAvailable || NaN);
      const free = Number(r.memFree || r.memoryFree || NaN);
      let usedPct = 0;
      if (isFinite(used) && total > 0) {
        usedPct = (used / total) * 100;
      } else if (isFinite(available) && total > 0) {
        usedPct = ((total - available) / total) * 100;
      } else if (isFinite(free) && total > 0) {
        usedPct = ((total - free) / total) * 100;
      } else if (isFinite(Number((r as any).memoryUsage))) {
        usedPct = Number((r as any).memoryUsage);
      }
      return { ...toPoint(r.timestamp), value: Math.max(0, Math.min(100, usedPct)) };
    });

    // Compute bandwidth (bps) from octet counters across interfaces
    const bpsByIso: Record<string, number> = {};
    if (ifaceGroups) {
      Object.values(ifaceGroups).forEach((records: any[]) => {
        const asc = [...records].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
        for (let i = 1; i < asc.length; i++) {
          const prev = asc[i - 1];
          const curr = asc[i];
          const tPrev = new Date(prev.timestamp).getTime();
          const tCurr = new Date(curr.timestamp).getTime();
          const dtSec = (tCurr - tPrev) / 1000;
          if (!isFinite(dtSec) || dtSec <= 0) continue;

          const inBitsPrev = Number(prev.inBits ?? ((prev.inOctets ?? prev.ifInOctets ?? 0) * 8));
          const inBitsCurr = Number(curr.inBits ?? ((curr.inOctets ?? curr.ifInOctets ?? 0) * 8));
          const outBitsPrev = Number(prev.outBits ?? ((prev.outOctets ?? prev.ifOutOctets ?? 0) * 8));
          const outBitsCurr = Number(curr.outBits ?? ((curr.outOctets ?? curr.ifOutOctets ?? 0) * 8));

          const dInBits = inBitsCurr - inBitsPrev;
          const dOutBits = outBitsCurr - outBitsPrev;
          if (dInBits < 0 || dOutBits < 0) continue; // counter reset/wrap

          const totalBits = dInBits + dOutBits;
          const bps = totalBits / dtSec;
          const iso = new Date(curr.timestamp).toISOString();
          bpsByIso[iso] = (bpsByIso[iso] || 0) + bps;
        }
      });
    }

    const network = Object.keys(bpsByIso)
      .sort()
      .map(iso => {
        const d = new Date(iso);
        return {
          time: d.toLocaleTimeString(),
          date: d.toLocaleDateString(),
          fullDate: iso,
          value: bpsByIso[iso] / 1_000_000, // bps -> Mbps
        };
      });

    return { cpu, memory, network };
  };

  const influxChartData = processInfluxForCharts();
  const chartData = influxChartData || processMetricsForCharts();

  // Extract device information from SNMP metrics
  const extractDeviceInfo = () => {
    if (!metricsData || !Array.isArray(metricsData)) {
      return {
        systemName: deviceData.name || "N/A",
        systemDescription: deviceMetadata.description || "N/A",
        uptime: "N/A",
        interfaces: 0,
        temperature: "N/A",
        diskUsage: "N/A"
      };
    }

    // Extract system information from SNMP metrics
    const sysNameMetric = metricsData.find((m: any) => m.metric_name === 'sysName');
    const sysDescrMetric = metricsData.find((m: any) => m.metric_name === 'sysDescr');
    const uptimeMetric = metricsData.find((m: any) => m.metric_name === 'uptime');
    
    // Count interfaces
    const interfaceMetrics = metricsData.filter((m: any) => m.metric_name?.startsWith('ifDescr_'));
    const interfaceCount = interfaceMetrics.length;
    
    // Extract temperature (if available)
    const tempMetrics = metricsData.filter((m: any) => m.metric_name?.startsWith('deviceTemp_'));
    const avgTemperature = tempMetrics.length > 0 
      ? (tempMetrics.reduce((sum: number, m: any) => sum + Number(m.value), 0) / tempMetrics.length).toFixed(1)
      : "N/A";
    
    // Extract disk usage
    const diskMetrics = metricsData.filter((m: any) => m.metric_name?.startsWith('diskUtil_'));
    const avgDiskUsage = diskMetrics.length > 0 
      ? (diskMetrics.reduce((sum: number, m: any) => sum + Number(m.value), 0) / diskMetrics.length).toFixed(1)
      : "N/A";

    // Format uptime
    const formatUptime = (uptimeSeconds: number) => {
      const days = Math.floor(uptimeSeconds / 86400);
      const hours = Math.floor((uptimeSeconds % 86400) / 3600);
      const minutes = Math.floor((uptimeSeconds % 3600) / 60);
      
      if (days > 0) return `${days}d ${hours}h ${minutes}m`;
      if (hours > 0) return `${hours}h ${minutes}m`;
      return `${minutes}m`;
    };

    return {
      systemName: sysNameMetric ? String(sysNameMetric.value) : deviceData.name || "N/A",
      systemDescription: sysDescrMetric ? String(sysDescrMetric.value) : deviceMetadata.description || "N/A",
      uptime: uptimeMetric ? formatUptime(Number(uptimeMetric.value)) : "N/A",
      interfaces: interfaceCount,
      temperature: avgTemperature !== "N/A" ? `${avgTemperature}°C` : "N/A",
      diskUsage: avgDiskUsage !== "N/A" ? `${avgDiskUsage}%` : "N/A"
    };
  };

  const deviceInfo = extractDeviceInfo();

  const getDeviceIcon = (type: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      "Network Router": <Router className="h-8 w-8" />,
      "Web Server": <Server className="h-8 w-8" />,
      "Database Server": <Database className="h-8 w-8" />,
      Firewall: <Shield className="h-8 w-8" />,
      Switch: <Network className="h-8 w-8" />,
      "Load Balancer": <LoaderIcon className="h-8 w-8" />,
    };

    return iconMap[type] || <Box className="h-8 w-8" />;
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case "up":
        return "text-secondary";
      case "down":
        return "text-danger";
      case "warning":
        return "text-warning";
      default:
        return "text-gray-500";
    }
  };

  const getTestStatusBadge = (status: string) => {
    switch (status) {
      case "success":
        return (
          <Badge className="bg-green-500">
            <CheckCircle2 className="mr-1 h-3 w-3" /> Success
          </Badge>
        );
      case "failed":
        return (
          <Badge variant="destructive">
            <AlertCircle className="mr-1 h-3 w-3" /> Failed
          </Badge>
        );
      case "running":
        return (
          <Badge className="bg-blue-500">
            <LoaderIcon className="mr-1 h-3 w-3 animate-spin" /> Running
          </Badge>
        );
      default:
        return (
          <Badge variant="outline">
            <Clock className="mr-1 h-3 w-3" /> {status}
          </Badge>
        );
    }
  };

  // Format metrics data for charts (legacy function for compatibility)

  return (
    <div>
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center">
          <div className="flex items-center">
            <span
              className={`mr-3 ${getStatusClass(
                ((influxSummary?.isOnline !== undefined)
                  ? (influxSummary?.isOnline ? "up" : "down")
                  : (deviceData?.status as "up" | "down" | "warning" | "unknown"))
              )}`}
            >
              {getDeviceIcon(deviceData?.type || "")}
            </span>
            <div>
              <h1 className="text-2xl font-semibold text-neutral-dark flex items-center">
                {deviceData?.name || "No data available"}
                <StatusBadge
                  status={
                    ((influxSummary?.isOnline !== undefined)
                      ? (influxSummary?.isOnline ? "up" : "down")
                      : (deviceData?.status as "up" | "down" | "warning" | "unknown"))
                  }
                  className="ml-3"
                />
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                {deviceData.ipAddress} | {deviceData.type} | Last checked{" "}
                {(() => {
                  try {
                    const lastCheck = influxSummary?.lastUpdate || deviceData.lastCheck;
                    if (!lastCheck) return "Never";
                    const date = new Date(lastCheck);
                    if (isNaN(date.getTime())) return "Invalid date";
                    return formatDistanceToNow(date, { addSuffix: true });
                  } catch (error) {
                    return "Unknown";
                  }
                })()}
              </p>
            </div>
          </div>
          <div className="mt-4 md:mt-0 flex space-x-2">
            <Button variant="outline" onClick={handleReboot}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reboot
            </Button>
            <Button asChild>
              <Link to={`/update-device/${deviceId}`}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Time Filter */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">Time Range:</span>
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="8h">8 Hours</SelectItem>
              <SelectItem value="12h">12 Hours</SelectItem>
              <SelectItem value="24h">24 Hours</SelectItem>
              <SelectItem value="1w">1 Week</SelectItem>
              <SelectItem value="1m">1 Month</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="text-sm text-gray-500">
          Showing data for {getTimeRangeLabel(timeRange)}
        </div>
      </div>

      {/* Device Details Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="alerts">Alerts</TabsTrigger>
          <TabsTrigger value="configuration">Configuration</TabsTrigger>
          <TabsTrigger value="tools">Tools</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Device Information */}
            <Card className="md:col-span-1">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Device Information</CardTitle>
                  {(!metricsData || !Array.isArray(metricsData) || metricsData.length === 0) && (
                    <div className="flex items-center text-sm text-amber-600">
                      <Info className="h-4 w-4 mr-1" />
                      Using device data
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {isLoadingDevice ? (
                  <div className="flex items-center justify-center h-20">
                    <Spinner />
                  </div>
                ) : (
                  <ul className="space-y-4">
                    <li className="flex justify-between">
                      <span className="text-sm text-gray-500">System Name</span>
                      <span className="text-sm font-medium">{deviceInfo.systemName}</span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-sm text-gray-500">Description</span>
                      <span className="text-sm font-medium max-w-xs truncate" title={deviceInfo.systemDescription}>
                        {deviceInfo.systemDescription}
                      </span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-sm text-gray-500">Uptime</span>
                      <span className="text-sm font-medium">{deviceInfo.uptime}</span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-sm text-gray-500">Interfaces</span>
                      <span className="text-sm font-medium">{influxSummary?.activeInterfaces ?? deviceInfo.interfaces}</span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-sm text-gray-500">Temperature</span>
                      <span className="text-sm font-medium">{deviceInfo.temperature}</span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-sm text-gray-500">Disk Usage</span>
                      <span className="text-sm font-medium">{influxSummary?.diskUsage !== undefined ? `${Math.round((influxSummary?.diskUsage || 0) as number)}%` : deviceInfo.diskUsage}</span>
                    </li>
                    {influxSummary?.currentPing !== undefined && (
                    <li className="flex justify-between">
                      <span className="text-sm text-gray-500">Ping</span>
                      <span className="text-sm font-medium">{influxSummary?.currentPing} ms</span>
                    </li>
                    )}
                    <li className="flex justify-between">
                      <span className="text-sm text-gray-500">IP Address</span>
                      <span className="text-sm font-medium">{deviceData.ipAddress}</span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-sm text-gray-500">Type</span>
                      <span className="text-sm font-medium">{deviceData.type}</span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-sm text-gray-500">Group</span>
                      <span className="text-sm font-medium">{deviceData.group}</span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-sm text-gray-500">Status</span>
                      <span className="text-sm font-medium">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          deviceData.status === 'up' ? 'bg-green-100 text-green-800' :
                          deviceData.status === 'down' ? 'bg-red-100 text-red-800' :
                          deviceData.status === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {deviceData.status}
                        </span>
                      </span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-sm text-gray-500">Performance</span>
                      <PerformanceBar value={Math.round((influxSummary?.cpuUsage ?? deviceData.performance) as number)} />
                    </li>
                  </ul>
                )}
              </CardContent>
            </Card>

            {/* Recent Performance Charts */}
            <Card className="md:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Performance Summary ({getTimeRangeLabel(timeRange)})</CardTitle>
                  {(!metricsData || !Array.isArray(metricsData) || metricsData.length === 0) && (
                    <div className="flex items-center text-sm text-amber-600">
                      <Info className="h-4 w-4 mr-1" />
                      Using simulated data
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Performance Summary Cards */}
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-blue-600 font-medium">CPU</p>
                        <p className="text-2xl font-bold text-blue-800">
                          {chartData.cpu.length > 0 ? Math.round(chartData.cpu[chartData.cpu.length - 1]?.value || 0) : 0}%
                        </p>
                      </div>
                      <Database className="h-6 w-6 text-blue-400" />
                    </div>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-green-600 font-medium">Memory</p>
                        <p className="text-2xl font-bold text-green-800">
                          {chartData.memory.length > 0 ? Math.round(chartData.memory[chartData.memory.length - 1]?.value || 0) : 0}%
                        </p>
                      </div>
                      <HardDrive className="h-6 w-6 text-green-400" />
                    </div>
                  </div>
                  <div className="bg-orange-50 p-3 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-orange-600 font-medium">Network</p>
                        <p className="text-2xl font-bold text-orange-800">
                          {chartData.network.length > 0 ? (chartData.network[chartData.network.length - 1]?.value || 0).toFixed(1) : 0} Mbps
                        </p>
                      </div>
                      <Activity className="h-6 w-6 text-orange-400" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium">CPU Utilization</h3>
                    {chartData.cpu.length > 0 ? (
                      <AreaChart
                        title=""
                        data={chartData.cpu}
                        color="#1a56db"
                        height={120}
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center h-[120px] bg-gray-50 rounded-lg">
                        {isChartLoading ? (
                          <>
                            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mb-2"></div>
                            <p className="text-xs text-gray-400">Loading data...</p>
                          </>
                        ) : (
                          <div className="flex flex-col items-center">
                            <BarChart3 className="h-8 w-8 text-gray-400 mb-2" />
                            <p className="text-sm text-gray-500">No data available</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium">Memory Usage</h3>
                    {chartData.memory.length > 0 ? (
                      <AreaChart
                        title=""
                        data={chartData.memory}
                        color="#10b981"
                        height={120}
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center h-[120px] bg-gray-50 rounded-lg">
                        {isChartLoading ? (
                          <>
                            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mb-2"></div>
                            <p className="text-xs text-gray-400">Loading data...</p>
                          </>
                        ) : (
                          <div className="flex flex-col items-center">
                            <Database className="h-8 w-8 text-gray-400 mb-2" />
                            <p className="text-sm text-gray-500">No data available</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium mb-2">Network Traffic</h3>
                  {chartData.network.length > 0 ? (
                    <AreaChart
                      title=""
                      data={chartData.network}
                      color="#f97316"
                      height={120}
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center h-[120px] bg-gray-50 rounded-lg">
                      {isChartLoading ? (
                        <>
                          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mb-2"></div>
                          <p className="text-xs text-gray-400">Loading data...</p>
                        </>
                      ) : (
                        <div className="flex flex-col items-center">
                          <Router className="h-8 w-8 text-gray-400 mb-2" />
                          <p className="text-sm text-gray-500">No data available</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* System Health Summary */}
          <Card>
            <CardHeader>
              <CardTitle>System Health</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${
                    deviceInfo.temperature !== "N/A" && parseFloat(deviceInfo.temperature) > 70 
                      ? 'bg-red-100' : 'bg-green-100'
                  }`}>
                    {deviceInfo.temperature !== "N/A" && parseFloat(deviceInfo.temperature) > 70 ? (
                      <AlertTriangle className="h-8 w-8 text-red-600" />
                    ) : (
                      <CheckCircle2 className="h-8 w-8 text-green-600" />
                    )}
                  </div>
                  <p className="text-sm font-medium mt-2">Temperature</p>
                  <p className="text-xs text-gray-500">{deviceInfo.temperature}</p>
                </div>
                
                <div className="text-center">
                  <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${
                    (influxSummary?.diskUsage !== undefined ? Number(influxSummary?.diskUsage) : (deviceInfo.diskUsage !== "N/A" ? parseFloat(deviceInfo.diskUsage) : 0)) > 80 
                      ? 'bg-red-100' : 'bg-green-100'
                  }`}>
                    {(influxSummary?.diskUsage !== undefined ? Number(influxSummary?.diskUsage) : (deviceInfo.diskUsage !== "N/A" ? parseFloat(deviceInfo.diskUsage) : 0)) > 80 ? (
                      <AlertTriangle className={`h-8 w-8 text-red-600`} />
                    ) : (
                      <CheckCircle2 className={`h-8 w-8 text-green-600`} />
                    )}
                  </div>
                  <p className="text-sm font-medium mt-2">Disk Usage</p>
                  <p className="text-xs text-gray-500">{influxSummary?.diskUsage !== undefined ? `${Math.round(Number(influxSummary?.diskUsage))}%` : deviceInfo.diskUsage}</p>
                </div>
                
                <div className="text-center">
                  <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${
                    chartData.cpu.length > 0 && chartData.cpu[chartData.cpu.length - 1]?.value > 80 
                      ? 'bg-red-100' : 'bg-green-100'
                  }`}>
                    {chartData.cpu.length > 0 && chartData.cpu[chartData.cpu.length - 1]?.value > 80 ? (
                      <AlertTriangle className={`h-8 w-8 text-red-600`} />
                    ) : (
                      <CheckCircle2 className={`h-8 w-8 text-green-600`} />
                    )}
                  </div>
                  <p className="text-sm font-medium mt-2">CPU Load</p>
                  <p className="text-xs text-gray-500">
                    {chartData.cpu.length > 0 ? Math.round(chartData.cpu[chartData.cpu.length - 1]?.value || 0) : 0}%
                  </p>
                </div>
                
                <div className="text-center">
                  <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${
                    chartData.memory.length > 0 && chartData.memory[chartData.memory.length - 1]?.value > 85 
                      ? 'bg-red-100' : 'bg-green-100'
                  }`}>
                    {chartData.memory.length > 0 && chartData.memory[chartData.memory.length - 1]?.value > 85 ? (
                      <AlertTriangle className={`h-8 w-8 text-red-600`} />
                    ) : (
                      <CheckCircle2 className={`h-8 w-8 text-green-600`} />
                    )}
                  </div>
                  <p className="text-sm font-medium mt-2">Memory</p>
                  <p className="text-xs text-gray-500">
                    {chartData.memory.length > 0 ? Math.round(chartData.memory[chartData.memory.length - 1]?.value || 0) : 0}%
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Interface Information */}
          {(influxInterfaceGroups && Object.keys(influxInterfaceGroups).length > 0) ? (
            <Card>
              <CardHeader>
                <CardTitle>Network Interfaces ({Object.keys(influxInterfaceGroups).length})</CardTitle>
              </CardHeader>
              <CardContent>
                {shouldVirtualizeInterfaces ? (
                  <div
                    ref={interfacesParentRef}
                    className="h-[400px] overflow-auto"
                  >
                    <div
                      style={{
                        height: `${interfacesVirtualizer.getTotalSize()}px`,
                        width: '100%',
                        position: 'relative',
                      }}
                    >
                      {interfacesVirtualizer.getVirtualItems().map((virtualItem) => {
                      const [ifaceName, records] = interfaceEntries[virtualItem.index];
                      const asc = [...(records as any[])].sort((a: any, b: any) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
                      const latest = asc[asc.length - 1] as any;
                      const prev = asc.length > 1 ? asc[asc.length - 2] as any : undefined;
                      let inMbps = 0;
                      let outMbps = 0;
                      if (latest && prev) {
                        const tPrev = new Date(prev.timestamp).getTime();
                        const tCurr = new Date(latest.timestamp).getTime();
                        const dtSec = (tCurr - tPrev) / 1000;
                        if (dtSec > 0) {
                          const inBitsPrev = Number(prev.inBits ?? ((prev.inOctets ?? prev.ifInOctets ?? 0) * 8));
                          const inBitsCurr = Number(latest.inBits ?? ((latest.inOctets ?? latest.ifInOctets ?? 0) * 8));
                          const outBitsPrev = Number(prev.outBits ?? ((prev.outOctets ?? prev.ifOutOctets ?? 0) * 8));
                          const outBitsCurr = Number(latest.outBits ?? ((latest.outOctets ?? latest.ifOutOctets ?? 0) * 8));

                          inMbps = Math.max(0, (inBitsCurr - inBitsPrev) / dtSec) / 1_000_000;
                          outMbps = Math.max(0, (outBitsCurr - outBitsPrev) / dtSec) / 1_000_000;
                        }
                      }
                      const isActive = Boolean(latest?.isActive ?? latest?.operStatus === 1);
                      const statusText = isActive ? 'Up' : 'Down';
                      const statusColor = isActive ? 'text-green-600' : 'text-red-600';
                      return (
                        <div
                          key={ifaceName}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: `${virtualItem.size}px`,
                            transform: `translateY(${virtualItem.start}px)`,
                          }}
                          className="mb-3"
                        >
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center space-x-3">
                              <Activity className="h-5 w-5 text-gray-400" />
                              <div>
                                <p className="text-sm font-medium">{ifaceName}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className={`text-sm font-medium ${statusColor}`}>{statusText}</p>
                              <p className="text-xs text-gray-500">
                                {inMbps.toFixed(2)} Mbps in / {outMbps.toFixed(2)} Mbps out
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                ) : (
                  <div className="space-y-3">
                    {Object.entries(influxInterfaceGroups).map(([ifaceName, records]) => {
                      const asc = [...records].sort((a: any, b: any) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
                      const latest = asc[asc.length - 1] as any;
                      const prev = asc.length > 1 ? asc[asc.length - 2] as any : undefined;
                      let inMbps = 0;
                      let outMbps = 0;
                      if (latest && prev) {
                        const tPrev = new Date(prev.timestamp).getTime();
                        const tCurr = new Date(latest.timestamp).getTime();
                        const dtSec = (tCurr - tPrev) / 1000;
                        if (dtSec > 0) {
                          const inBitsPrev = Number(prev.inBits ?? ((prev.inOctets ?? prev.ifInOctets ?? 0) * 8));
                          const inBitsCurr = Number(latest.inBits ?? ((latest.inOctets ?? latest.ifInOctets ?? 0) * 8));
                          const outBitsPrev = Number(prev.outBits ?? ((prev.outOctets ?? prev.ifOutOctets ?? 0) * 8));
                          const outBitsCurr = Number(latest.outBits ?? ((latest.outOctets ?? latest.ifOutOctets ?? 0) * 8));

                          inMbps = Math.max(0, (inBitsCurr - inBitsPrev) / dtSec) / 1_000_000;
                          outMbps = Math.max(0, (outBitsCurr - outBitsPrev) / dtSec) / 1_000_000;
                        }
                      }
                      const isActive = Boolean(latest?.isActive ?? latest?.operStatus === 1);
                      const statusText = isActive ? 'Up' : 'Down';
                      const statusColor = isActive ? 'text-green-600' : 'text-red-600';
                      return (
                        <div key={ifaceName} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <Activity className="h-5 w-5 text-gray-400" />
                            <div>
                              <p className="text-sm font-medium">{ifaceName}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`text-sm font-medium ${statusColor}`}>{statusText}</p>
                            <p className="text-xs text-gray-500">
                              {inMbps.toFixed(2)} Mbps in / {outMbps.toFixed(2)} Mbps out
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            deviceInfo.interfaces > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Network Interfaces ({deviceInfo.interfaces})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {metricsData && Array.isArray(metricsData) && 
                      metricsData
                        .filter((m: any) => m.metric_name?.startsWith('ifDescr_'))
                        .map((interfaceMetric: any) => {
                          const interfaceIndex = interfaceMetric.metric_name.split('_')[1];
                          const interfaceName = interfaceMetric.value;
                          const statusMetric = metricsData.find((m: any) => m.metric_name === `ifOperStatus_${interfaceIndex}`);
                          const status = statusMetric ? Number(statusMetric.value) : 0;
                          const statusText = status === 1 ? 'Up' : status === 2 ? 'Down' : 'Testing';
                          const statusColor = status === 1 ? 'text-green-600' : status === 2 ? 'text-red-600' : 'text-yellow-600';
                          const inOctetsMetric = metricsData.find((m: any) => m.metric_name === `ifInOctets_${interfaceIndex}`);
                          const outOctetsMetric = metricsData.find((m: any) => m.metric_name === `ifOutOctets_${interfaceIndex}`);
                          const inTraffic = inOctetsMetric ? (Number(inOctetsMetric.value) / 1000000).toFixed(2) : '0';
                          const outTraffic = outOctetsMetric ? (Number(outOctetsMetric.value) / 1000000).toFixed(2) : '0';
                          return (
                            <div key={interfaceIndex} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center space-x-3">
                                <Activity className="h-5 w-5 text-gray-400" />
                                <div>
                                  <p className="text-sm font-medium">{interfaceName}</p>
                                  <p className="text-xs text-gray-500">Interface {interfaceIndex}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className={`text-sm font-medium ${statusColor}`}>{statusText}</p>
                                <p className="text-xs text-gray-500">
                                  {inTraffic} Mbps in / {outTraffic} Mbps out
                                </p>
                              </div>
                            </div>
                          );
                        })
                    }
                  </div>
                </CardContent>
              </Card>
            )
          )}

          {/* Notes Section */}
          <Card>
            <CardHeader>
              <CardTitle>Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                {deviceMetadata.notes || "No notes available for this device."}
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance" className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <h3 className="text-lg font-medium mb-4">CPU Utilization ({getTimeRangeLabel(timeRange)})</h3>
              {chartData.cpu.length > 0 ? (
                <AreaChart
                  title=""
                  data={chartData.cpu}
                  color="#1a56db"
                  height={300}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-[300px] bg-gray-50 rounded-lg">
                  {isChartLoading ? (
                    <>
                      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-3"></div>
                      <p className="text-sm text-gray-400">Loading data...</p>
                    </>
                  ) : (
                    <div className="flex flex-col items-center">
                      <BarChart3 className="h-12 w-12 text-gray-400 mb-3" />
                      <p className="text-base text-gray-500">No CPU utilization data available</p>
                      <p className="text-sm text-gray-400 mt-1">Try adjusting the time range or check device connection</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <h3 className="text-lg font-medium mb-4">Memory Usage ({getTimeRangeLabel(timeRange)})</h3>
              {chartData.memory.length > 0 ? (
                <AreaChart
                  title=""
                  data={chartData.memory}
                  color="#10b981"
                  height={300}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-[300px] bg-gray-50 rounded-lg">
                  {isChartLoading ? (
                    <>
                      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-3"></div>
                      <p className="text-sm text-gray-400">Loading data...</p>
                    </>
                  ) : (
                    <div className="flex flex-col items-center">
                      <Database className="h-12 w-12 text-gray-400 mb-3" />
                      <p className="text-base text-gray-500">No memory usage data available</p>
                      <p className="text-sm text-gray-400 mt-1">Try adjusting the time range or check device connection</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <h3 className="text-lg font-medium mb-4">Network Traffic ({getTimeRangeLabel(timeRange)})</h3>
              {chartData.network.length > 0 ? (
                <AreaChart
                  title=""
                  data={chartData.network}
                  color="#f97316"
                  height={300}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-[300px] bg-gray-50 rounded-lg">
                  {isChartLoading ? (
                    <>
                      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-3"></div>
                      <p className="text-sm text-gray-400">Loading data...</p>
                    </>
                  ) : (
                    <div className="flex flex-col items-center">
                      <Router className="h-12 w-12 text-gray-400 mb-3" />
                      <p className="text-base text-gray-500">No network traffic data available</p>
                      <p className="text-sm text-gray-400 mt-1">Try adjusting the time range or check device connection</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent Alerts</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingAlerts ? (
                <div className="flex items-center justify-center h-20">
                  <Spinner />
                </div>
              ) : !deviceAlerts || deviceAlerts.length === 0 ? (
                <div className="py-6 text-center">
                  <BellOff className="h-8 w-8 text-gray-400 mb-2" />
                  <p className="text-gray-500">No alerts for this device</p>
                </div>
              ) : shouldVirtualizeAlerts ? (
                <div
                  ref={alertsParentRef}
                  className="h-[600px] overflow-auto"
                >
                  <div
                    style={{
                      height: `${alertsVirtualizer.getTotalSize()}px`,
                      width: '100%',
                      position: 'relative',
                    }}
                  >
                    {alertsVirtualizer.getVirtualItems().map((virtualItem) => {
                      const alert = deviceAlerts[virtualItem.index];
                      const isWarning = alert.severity === "warning";
                      const isInfo = alert.severity === "info";
                      const isCritical = alert.severity === "critical";
                      
                      let iconClass = "text-blue-500";
                      let bgClass = "bg-blue-100";
                      let icon: React.ReactNode = <Info className="h-5 w-5" />;
                      
                      if (isWarning) {
                        iconClass = "text-warning";
                        bgClass = "bg-warning bg-opacity-10";
                        icon = <AlertTriangle className="h-5 w-5" />;
                      } else if (isCritical) {
                        iconClass = "text-danger";
                        bgClass = "bg-danger bg-opacity-10";
                        icon = <AlertCircle className="h-5 w-5" />;
                      }
                      
                      return (
                        <div
                          key={alert.id}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: `${virtualItem.size}px`,
                            transform: `translateY(${virtualItem.start}px)`,
                          }}
                          className="border-b border-gray-200"
                        >
                          <div className="py-4 px-1">
                            <div className="flex items-start">
                              <div
                                className={`rounded-full w-8 h-8 flex items-center justify-center ${bgClass} mt-1`}
                              >
                                <span className={iconClass}>
                                  {icon}
                                </span>
                              </div>
                              <div className="ml-4 flex-1">
                                <div className="flex items-center justify-between">
                                  <div className="text-sm font-medium text-neutral-dark">
                                    {alert.message}
                                  </div>
                                  <div className="ml-2">
                                    <span
                                      className={`px-2 py-1 inline-flex text-xs leading-4 font-semibold rounded-full ${
                                        isWarning
                                          ? "bg-warning bg-opacity-10 text-warning"
                                          : isInfo
                                          ? "bg-blue-100 text-blue-800"
                                          : "bg-danger bg-opacity-10 text-danger"
                                      }`}
                                    >
                                      {alert.severity.charAt(0).toUpperCase() +
                                        alert.severity.slice(1)}
                                    </span>
                                  </div>
                                </div>
                                <p className="mt-1 text-sm text-gray-500">
                                  {alert.details}
                                </p>
                                <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                                  <span>
                                    {formatDistanceToNow(
                                      new Date(alert.timestamp),
                                      { addSuffix: true }
                                    )}
                                  </span>
                                  <div className="flex space-x-2">
                                    {!alert.acknowledged && (
                                      <Button
                                        size="sm"
                                        className="h-7 text-xs px-2"
                                      >
                                        Acknowledge
                                      </Button>
                                    )}
                                    {!alert.resolved && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 text-xs px-2"
                                      >
                                        Resolve
                                      </Button>
                                    )}
                                    {alert.acknowledged && alert.resolved && (
                                      <span className="text-secondary flex items-center">
                                        <CheckCircle2 className="h-3 w-3 mr-1" />
                                        Resolved
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {deviceAlerts && deviceAlerts.length > 0 && deviceAlerts.map((alert) => {
                    const isWarning = alert.severity === "warning";
                    const isInfo = alert.severity === "info";
                    const isCritical = alert.severity === "critical";
                    
                    let iconClass = "text-blue-500";
                    let bgClass = "bg-blue-100";
                    let icon: React.ReactNode = <Info className="h-5 w-5" />;
                    
                    if (isWarning) {
                      iconClass = "text-warning";
                      bgClass = "bg-warning bg-opacity-10";
                      icon = <AlertTriangle className="h-5 w-5" />;
                    } else if (isCritical) {
                      iconClass = "text-danger";
                      bgClass = "bg-danger bg-opacity-10";
                      icon = <AlertCircle className="h-5 w-5" />;
                    }
                    
                    return (
                      <li key={alert.id} className="py-4">
                        <div className="flex items-start">
                          <div
                            className={`rounded-full w-8 h-8 flex items-center justify-center ${bgClass} mt-1`}
                          >
                            <span className={iconClass}>
                              {icon}
                            </span>
                          </div>
                          <div className="ml-4 flex-1">
                            <div className="flex items-center justify-between">
                              <div className="text-sm font-medium text-neutral-dark">
                                {alert.message}
                              </div>
                              <div className="ml-2">
                                <span
                                  className={`px-2 py-1 inline-flex text-xs leading-4 font-semibold rounded-full ${
                                    isWarning
                                      ? "bg-warning bg-opacity-10 text-warning"
                                      : isInfo
                                      ? "bg-blue-100 text-blue-800"
                                      : "bg-danger bg-opacity-10 text-danger"
                                  }`}
                                >
                                  {alert.severity.charAt(0).toUpperCase() +
                                    alert.severity.slice(1)}
                                </span>
                              </div>
                            </div>
                            <p className="mt-1 text-sm text-gray-500">
                              {alert.details}
                            </p>
                            <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                              <span>
                                {formatDistanceToNow(
                                  new Date(alert.timestamp),
                                  { addSuffix: true }
                                )}
                              </span>
                              <div className="flex space-x-2">
                                {!alert.acknowledged && (
                                  <Button
                                    size="sm"
                                    className="h-7 text-xs px-2"
                                  >
                                    Acknowledge
                                  </Button>
                                )}
                                {!alert.resolved && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-xs px-2"
                                  >
                                    Resolve
                                  </Button>
                                )}
                                {alert.acknowledged && alert.resolved && (
                                  <span className="text-secondary flex items-center">
                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                    Resolved
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Configuration Tab */}
        <TabsContent value="configuration" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Configuration History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="py-6 text-center">
                <History className="h-8 w-8 text-gray-400 mb-2" />
                <p className="text-gray-500">Configuration history not available in the MVP</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tools Tab */}
        <TabsContent value="tools" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Network Tools</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Configuration */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1 space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Target Device</p>
                    <p className="text-sm text-gray-500">
                      {deviceData.name} ({deviceData.ipAddress})
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="tool-test-type">Test Type</Label>
                    <Select
                      value={selectedTestType}
                      onValueChange={setSelectedTestType}
                    >
                      <SelectTrigger id="tool-test-type">
                        <SelectValue placeholder="Select test type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ping">Ping Test</SelectItem>
                        <SelectItem value="traceroute">Traceroute</SelectItem>
                        <SelectItem value="dns">DNS Lookup</SelectItem>
                        <SelectItem value="http">HTTP Check</SelectItem>
                        <SelectItem value="port">Port Check</SelectItem>
                        <SelectItem value="snmp">SNMP Test</SelectItem>
                        <SelectItem value="ssh">SSH Test</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    className="w-full"
                    onClick={() => runTestMutation.mutate({ testType: selectedTestType })}
                    disabled={runTestMutation.isPending}
                  >
                    {runTestMutation.isPending ? (
                      <>
                        <LoaderIcon className="mr-2 h-4 w-4 animate-spin" />
                        Running Test...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Run Test
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-gray-500">
                    Tests are executed against this device using the backend `runNetworkTest`
                    endpoint. SNMP/SSH tests use the device&apos;s stored credentials.
                  </p>
                </div>

                {/* Results */}
                <div className="lg:col-span-2">
                  {testResults.length === 0 ? (
                    <div className="text-center py-10 text-muted-foreground">
                      <Terminal className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
                      <p>No tests run yet for this device.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <AnimatePresence mode="popLayout">
                        {testResults.map((result) => (
                          <motion.div
                            key={result.id}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.96 }}
                            transition={{ duration: 0.18 }}
                          >
                            <Card className="overflow-hidden">
                              <CardHeader className="py-3">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <div className="flex items-center space-x-2">
                                      <span className="text-sm font-medium capitalize">
                                        {result.testType} Test
                                      </span>
                                      <span className="text-xs text-muted-foreground">
                                        • {deviceData.name}
                                      </span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {(() => {
                                        try {
                                          return formatDistanceToNow(
                                            new Date(result.timestamp),
                                            { addSuffix: true }
                                          );
                                        } catch {
                                          return "Unknown time";
                                        }
                                      })()}
                                    </p>
                                  </div>
                                  <div>{getTestStatusBadge(result.status)}</div>
                                </div>
                              </CardHeader>
                              <Separator />
                              <CardContent className="py-3">
                                <div className="space-y-2">
                                  <div className="flex items-center text-sm">
                                    <span className="font-medium">Command:</span>
                                    <code className="ml-2 px-1 py-0.5 bg-muted rounded text-xs">
                                      {result.command}
                                    </code>
                                  </div>
                                  <div className="bg-muted rounded p-2 my-2 max-h-60 overflow-auto font-mono text-xs whitespace-pre w-full">
                                    {result.output || "No output available"}
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
